import { Icon, IconType } from '@lmc/ui-kit'
import { ConfigProvider, Menu } from 'antd'
import useCarer from 'hooks/useCarer'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import {
  CustomMenuItemType,
  getNavigationMenuItems,
  navigationTheme,
} from 'utils'
import TeamMemberSideNavigationCard from './TeamMemberSideNavigationCard'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { Home, User, UserRole } from '@lmc/ts-common'
import { useSelector } from 'react-redux'
import { useTeamMemberSidebarNavigation } from './utils'
import useHomeType from 'hooks/useHomeType'
import { ReactNode } from 'react'

type TeamSidebarNavigationFlagProps = {
  skillsAndNeedsGranted?: boolean
  proEnabled?: boolean
  hasRota?: boolean
  isNotGroupRole: boolean
}

const getMenuItems = ({
  skillsAndNeedsGranted,
  proEnabled,
  hasRota,
  isNotGroupRole,
}: TeamSidebarNavigationFlagProps) => {
  return [
    {
      label: 'Profile',
      url: 'profile',
      icon: <Icon customIcon={IconType.SideProfile} />,
    },
    ...(skillsAndNeedsGranted
      ? [
          {
            label: 'Training and Skills',
            url: 'skills',
            icon: <Icon customIcon={IconType.TrainingAndSkillsIcon} />,
          },
        ]
      : []),
    ...(proEnabled
      ? [
          {
            label: hasRota ? 'Contract and Preferences' : 'Contract',
            url: 'contract-and-preferences',
            icon: <Icon customIcon={IconType.ContractAndPreferencesIcon} />,
            id: 'contract-and-preferences-menu-item',
            'data-intercom-target': 'contract-and-preferences-menu-item',
          },
        ]
      : []),
    ...(hasRota && isNotGroupRole
      ? [
          {
            label: 'Absences',
            url: 'absences',
            icon: <Icon customIcon={IconType.AbsencesIcon} />,
            id: 'absences-menu-item',
            'data-intercom-target': 'absences-menu-item',
          },
        ]
      : []),
  ]
}

const TeamMemberSideNavigation = ({ testId }: { testId?: string }) => {
  const navigate = useNavigate()
  const { id } = useParams()

  const user = useSelector(
    (state: { global: { user: User } }) => state.global.user,
  )

  const { hasRota } = useHomeType()

  const isNotGroupRole = ![
    UserRole.groupAdmin,
    UserRole.groupManager,
  ]?.includes(user.role)

  const { carer } = useCarer(isNotGroupRole ? id : undefined)

  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home,
  )

  const items = getNavigationMenuItems(
    getMenuItems({
      skillsAndNeedsGranted: home?.features?.skillsAndNeeds?.granted,
      proEnabled: home?.features?.pro?.enabled,
      hasRota,
      isNotGroupRole,
    }) as unknown as CustomMenuItemType[],
  ) as CustomMenuItemType[]

  const { defaultSelectedKey } = useTeamMemberSidebarNavigation()

  return (
    <SidebarContainer data-testid={testId}>
      {carer && (
        <TeamMemberSideNavigationCard
          carer={carer}
          testId={testId ? `${testId}-card` : undefined}
        />
      )}
      <Divider data-testid={testId ? `${testId}-divider` : undefined} />
      <ConfigProvider theme={navigationTheme}>
        <Menu
          data-cy="team-members-menu"
          onClick={({ key }) => navigate(`${id}/${key}`)}
          style={{ border: 'none' }}
          defaultSelectedKeys={[defaultSelectedKey]}
          mode="inline"
          theme="light"
          inlineCollapsed={false}
          items={items}
          data-testid={testId ? `${testId}-menu` : undefined}
        />
      </ConfigProvider>
    </SidebarContainer>
  )
}
const SidebarContainer = styled.div`
  margin-bottom: 0px;
  background-color: ${(props) => props.theme.color.neutral.color11};
  max-width: 290px;
  width: 100%;
  height: 100%;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin-right: 24px;
    margin-left: 24px;
    max-width: unset;
    width: 100%;
  }
  overflow: auto;
`

export default TeamMemberSideNavigation
